<template>
    <div class="ecoscore-truck-view">
        <div class="ecoscore-truck-view__header">
            <div>
                <h1>{{ $t('title') }}</h1>
                <p class="t-subtle">
                    {{ $tc('trucks', trucksFiltered.length) }}
                </p>
            </div>

            <div class="ecoscore-truck-view__controls">
                <BaseInput
                    v-model="search"
                    :debounce="400"
                    :placeholder="$t('searchPlaceholder')"
                    block
                >
                    <template #icon>
                        <SearchIcon width="16" height="16" />
                    </template>
                </BaseInput>

                <SegmentButton v-model="days" :options="timeDurationOptions" />
            </div>
        </div>

        <div v-if="isEcoscoreTrucksLoading" class="l-padded l-inline l-center">
            <VSpinner size="medium" :line-fg-color="spinnerColor" :speed="1" />
        </div>

        <EcoScoreTruckTable
            v-else
            :trucks="trucksFiltered"
            class="ecoscore-truck-view__content"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import VSpinner from 'vue-simple-spinner'

import BaseInput from '@/components/redesigned/BaseInput'
import EcoScoreTruckTable from '@/components/ecoscore/EcoScoreTruckTable'
import SearchIcon from '@/components/icons/SearchIcon'
import SegmentButton from '@/components/redesigned/SegmentButton'

export default {
    name: 'EcoScoreTruckView',
    components: {
        BaseInput,
        EcoScoreTruckTable,
        SearchIcon,
        SegmentButton,
        VSpinner,
    },
    data() {
        return {
            days: [10],
            search: '',
            spinnerColor: process.env.VUE_APP_COLOR_PRIMARY,
            timeDurationOptions: [
                { value: 1, label: this.$tc('days', 1) },
                { value: 10, label: this.$tc('days', 10) },
                { value: 30, label: this.$tc('days', 30) },
            ],
        }
    },
    computed: {
        ...mapState('ecoscore', ['isEcoscoreTrucksLoading']),
        ...mapGetters('ecoscore', ['ecoscoreTrucksByDays']),
        trucks() {
            return this.ecoscoreTrucksByDays(this.days[0])
        },
        trucksFiltered() {
            if (!this.search) {
                return this.trucks
            }
            const query = this.search.toLowerCase()
            return this.trucks.filter(truck =>
                truck.name.toLowerCase().includes(query)
            )
        },
    },
    mounted() {
        this.loadEcoscoreTrucks()
    },
    methods: {
        ...mapActions('ecoscore', ['loadEcoscoreTrucks']),
    },
}
</script>

<i18n>
{
    "en": {
        "days": "{n} Day | {n} Days",
        "searchPlaceholder": "Search trucks",
        "title": "Trucks",
        "trucks": "{n} Truck | {n} Trucks"
    },
    "de": {
        "days": "{n} Tag | {n} Tage",
        "searchPlaceholder": "LKW suchen",
        "title": "LKW",
        "trucks": "{n} LKW"
    },
    "fr": {
        "days": "{n} Jour | {n} Jours",
        "searchPlaceholder": "Rechercher un camion",
        "title": "Camions",
        "trucks": "{n} Camion | {n} Camions"
    },
    "it": {
        "days": "{n} Giorno | {n} Giorni",
        "searchPlaceholder": "Cerca camion",
        "title": "Camion",
        "trucks": "{n} Camion"
    }
}
</i18n>

<style lang="scss" scoped>
.ecoscore-truck-view {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;

    &__header {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding: 1.5rem 2rem;
        background-color: $color-gray-lighter-new;
        border-bottom: $style-border;

        h1 {
            font-size: 28px;
            color: $color-primary;
        }
    }

    &__controls {
        display: flex;
        align-items: center;
        gap: 0.75rem;

        .base-input {
            max-width: 200px;
        }

        .segment-button {
            white-space: nowrap;
        }
    }

    &__content {
        width: 100%;
        height: 100%;
        overflow-y: auto;
    }

    @include respond-to('for-mobile-down') {
        &__controls {
            flex-wrap: wrap;

            .base-input {
                max-width: 100%;
            }
        }
    }
}
</style>
